import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { businessInfoAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { can, PERMS } from '../../utils/accessControl';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { useAppStore } from '../../stores/useAppStore';

const BusinessInformationPage = () => {
  const { user } = useAuth();
  const { addNotification } = useAppStore();
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSMTPModal, setShowSMTPModal] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState(['']);
  const [dailyClosingEmails, setDailyClosingEmails] = useState(['']);
  const [cartReviewEmails, setCartReviewEmails] = useState(['']);
  const [cartReviewEnabled, setCartReviewEnabled] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_address: '',
    use_tls: true,
    use_ssl: false,
    // The server never sends the real password back; this flag tells us whether
    // one is already saved so we can show "leave blank to keep current".
    has_password: false
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      business_name: '',
      branch_location: '',
      business_address: '',
      business_phone: '',
      business_email: '',
      otp_expiry_minutes: 10,
      otp_length: 6
    }
  });

  // "Business information" permission (Admin by default)
  const canEdit = can(user, PERMS.BUSINESS_INFO);

  useEffect(() => {
    if (canEdit) {
      fetchBusinessInfo();
      loadLogo();
    }
  }, [canEdit]);

  const fetchBusinessInfo = async () => {
    try {
      setLoading(true);
      const response = await businessInfoAPI.get();
      const data = response.data;
      
      console.log('Business Info API Response:', data);
      setBusinessInfo(data);
      
      // Set form values - using UPPERCASE field names for consistency
      setValue('business_name', data.BusinessName || '');
      setValue('branch_location', data.BranchLocation || '');
      setValue('business_address', data.BusinessAddress || '');
      setValue('business_phone', data.BusinessPhone || '');
      setValue('business_email', data.BusinessEmail || '');
      setValue('otp_expiry_minutes', data.OTPExpiryMinutes || 10);
      setValue('otp_length', data.OTPLength || 6);

      // Handle notification emails array
      if (data.NotificationEmail && Array.isArray(data.NotificationEmail)) {
        setNotificationEmails(data.NotificationEmail.length > 0 ? data.NotificationEmail : ['']);
      } else {
        setNotificationEmails(['']);
      }

      // Handle daily closing emails array
      if (data.DailyClosingMailingList && Array.isArray(data.DailyClosingMailingList)) {
        setDailyClosingEmails(data.DailyClosingMailingList.length > 0 ? data.DailyClosingMailingList : ['']);
      } else {
        setDailyClosingEmails(['']);
      }

      // Handle cart review emails array
      if (data.CartReviewMailingList && Array.isArray(data.CartReviewMailingList)) {
        setCartReviewEmails(data.CartReviewMailingList.length > 0 ? data.CartReviewMailingList : ['']);
      } else {
        setCartReviewEmails(['']);
      }
      setCartReviewEnabled(!!data.CartReviewEmailEnabled);

      // Set SMTP config if exists. The server intentionally omits the password
      // for security, so we keep the password field blank and rely on
      // has_password to indicate that one is already stored.
      if (data.SMTPConfig) {
        setSmtpConfig({
          host: data.SMTPConfig.host || '',
          port: data.SMTPConfig.port || 587,
          username: data.SMTPConfig.username || '',
          password: '', // blank = keep the existing password on save
          from_address: data.SMTPConfig.from_address || '',
          use_tls: data.SMTPConfig.use_tls ?? true,
          use_ssl: data.SMTPConfig.use_ssl ?? false,
          has_password: !!data.SMTPConfig.has_password
        });
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
      addNotification({
        type: 'error',
        message: 'Failed to fetch business information'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLogo = async () => {
    try {
      const response = await businessInfoAPI.getLogo();
      console.log('Logo API Response:', response);
      // response.data is now a URL string instead of blob
      setLogoPreview(response.data);
    } catch (error) {
      // Logo doesn't exist yet, this is normal
      console.log('No logo found');
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        addNotification({
          type: 'error',
          message: 'Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.'
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        addNotification({
          type: 'error',
          message: 'File size too large. Maximum size is 5MB.'
        });
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      // Format payload to match the database model (UPPERCASE field names)
      const payload = {
        BusinessName: data.business_name,
        BranchLocation: data.branch_location,
        BusinessAddress: data.business_address,
        BusinessPhone: data.business_phone,
        BusinessEmail: data.business_email,
        NotificationEmail: notificationEmails.filter(email => email.trim()),
        DailyClosingMailingList: dailyClosingEmails.filter(email => email.trim()),
        CartReviewMailingList: cartReviewEmails.filter(email => email.trim()),
        CartReviewEmailEnabled: cartReviewEnabled,
        OTPExpiryMinutes: data.otp_expiry_minutes,
        OTPLength: data.otp_length,
        SMTPConfig: smtpConfig.host ? {
          host: smtpConfig.host,
          port: smtpConfig.port,
          username: smtpConfig.username,
          password: smtpConfig.password,
          from_address: smtpConfig.from_address,
          use_tls: smtpConfig.use_tls,
          use_ssl: smtpConfig.use_ssl
        } : null,
        Logo: logoFile // Include the logo file
      };

      // Redact the SMTP password before logging so it never lands in devtools.
      console.log('Sending payload:', {
        ...payload,
        SMTPConfig: payload.SMTPConfig
          ? { ...payload.SMTPConfig, password: payload.SMTPConfig.password ? '[redacted]' : '' }
          : null
      });
      const response = await businessInfoAPI.update(payload);
      setBusinessInfo(response.data);
      
      addNotification({
        type: 'success',
        message: 'Business information updated successfully'
      });

      // Hard reload to refresh cached business information
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Small delay to show the success message
    } catch (error) {
      console.error('Error updating business info:', error);
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to update business information'
      });
    } finally {
      setSaving(false);
    }
  };

  const addNotificationEmail = () => {
    setNotificationEmails(prev => [...prev, '']);
  };

  const removeNotificationEmail = (index) => {
    if (notificationEmails.length > 1) {
      setNotificationEmails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateNotificationEmail = (index, value) => {
    setNotificationEmails(prev => prev.map((email, i) => i === index ? value : email));
  };

  const addDailyClosingEmail = () => {
    setDailyClosingEmails(prev => [...prev, '']);
  };

  const removeDailyClosingEmail = (index) => {
    if (dailyClosingEmails.length > 1) {
      setDailyClosingEmails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateDailyClosingEmail = (index, value) => {
    setDailyClosingEmails(prev => prev.map((email, i) => i === index ? value : email));
  };

  const addCartReviewEmail = () => {
    setCartReviewEmails(prev => [...prev, '']);
  };

  const removeCartReviewEmail = (index) => {
    if (cartReviewEmails.length > 1) {
      setCartReviewEmails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateCartReviewEmail = (index, value) => {
    setCartReviewEmails(prev => prev.map((email, i) => i === index ? value : email));
  };

  const handleSMTPConfigChange = (field, value) => {
    setSmtpConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="text-red-500 text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">
              Your role can't open the business settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading business information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Business Information</h1>
          <p className="text-gray-600">
            Manage your business settings.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Business Information */}
          <Card>
            <CardHeader>
              <CardTitle>Business Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name
                  </label>
                  <Input
                    {...register('business_name')}
                    placeholder="Enter business name"
                    className={errors.business_name ? 'border-red-500' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Phone
                  </label>
                  <Input
                    {...register('business_phone')}
                    placeholder="Enter business phone"
                    className={errors.business_phone ? 'border-red-500' : ''}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch Location
                </label>
                <Input
                  {...register('branch_location')}
                  placeholder="e.g. Lahore - Emporium Mall"
                  className={errors.branch_location ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Identifies this counter in cart review emails. Leave blank to fall
                  back to the branch key from the server config.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Email
                </label>
                <Input
                  {...register('business_email')}
                  type="email"
                  placeholder="Enter business email"
                  className={errors.business_email ? 'border-red-500' : ''}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Address
                </label>
                <textarea
                  {...register('business_address')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter business address"
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Business Logo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-4">
                {logoPreview && (
                  <div className="flex-shrink-0">
                    <img
                      src={logoPreview}
                      alt="Business Logo Preview"
                      className="w-20 h-20 object-cover rounded-lg border border-gray-300"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Logo
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: JPEG, PNG, GIF, WebP. Maximum size: 5MB.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SMTP Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>SMTP Configuration</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSMTPModal(true)}
                >
                  Configure SMTP
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  SMTP configuration is used to send discount approval emails.
                </p>
                {smtpConfig && smtpConfig.host ? (
                  <div className="text-sm">
                    <p><strong>Host:</strong> {smtpConfig.host}</p>
                    <p><strong>Port:</strong> {smtpConfig.port}</p>
                    <p><strong>Username:</strong> {smtpConfig.username}</p>
                    <p><strong>From Address:</strong> {smtpConfig.from_address || 'Not set'}</p>
                    <p><strong>Use TLS:</strong> {smtpConfig.use_tls ? 'Yes' : 'No'}</p>
                    <p><strong>Password:</strong> {smtpConfig.has_password || smtpConfig.password ? '•••••••• (set)' : 'Not set'}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">No SMTP configuration set</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Email Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Email Settings</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addNotificationEmail}
                >
                  Add Email
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Emails
                </label>
                {notificationEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-2 w-full">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateNotificationEmail(index, e.target.value)}
                      placeholder="Enter email for discount approval notifications"
                      className="flex-1 w-full"
                    />
                    {notificationEmails.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeNotificationEmail(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-500">
                  These emails will receive OTP codes for discount approvals.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Daily Closing Email Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Daily Closing Email Settings</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addDailyClosingEmail}
                >
                  Add Email
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Closing Mailing List
                </label>
                {dailyClosingEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-2 w-full">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateDailyClosingEmail(index, e.target.value)}
                      placeholder="Enter email for daily closing reports"
                      className="flex-1 w-full"
                    />
                    {dailyClosingEmails.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeDailyClosingEmail(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-500">
                  These emails will receive daily closing reports.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cart Review Email Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Cart Review Email Settings</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCartReviewEmail}
                  disabled={!cartReviewEnabled}
                >
                  Add Email
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 w-full">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cartReviewEnabled}
                    onChange={(e) => setCartReviewEnabled(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">
                      Show the &quot;Send for Review&quot; button on the POS screen
                    </span>
                    <span className="block text-xs text-gray-500">
                      Lets a cashier email the open cart before the sale is placed, so
                      someone can look at the items and reply with the special discount
                      to apply.
                    </span>
                  </span>
                </label>

                <div className={cartReviewEnabled ? 'space-y-3' : 'space-y-3 opacity-50 pointer-events-none'}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                    Cart Review Mailing List
                  </label>
                  {cartReviewEmails.map((email, index) => (
                    <div key={index} className="flex items-center gap-2 w-full">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => updateCartReviewEmail(index, e.target.value)}
                        placeholder="Enter email to receive cart reviews"
                        className="flex-1 w-full"
                        disabled={!cartReviewEnabled}
                      />
                      {cartReviewEmails.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeCartReviewEmail(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    These emails receive the captured cart. Requires SMTP to be
                    configured above.
                  </p>
                </div>

                {cartReviewEnabled && !cartReviewEmails.some(email => email.trim()) && (
                  <p className="text-xs text-amber-600">
                    Add at least one email — the button stays hidden until someone is
                    listed here.
                  </p>
                )}
                {cartReviewEnabled && !(smtpConfig && smtpConfig.host) && (
                  <p className="text-xs text-amber-600">
                    No SMTP server configured — the button stays hidden until one is set.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Discount Limits: set on the roles (and per person) since roles came in */}
          <Card>
            <CardHeader>
              <CardTitle>Discount Limits</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700">
                Discount limits are set on each role, and a person can have their own limit.
              </p>
              {can(user, PERMS.USERS) && (
                <Link to="/users/roles" className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-700">
                  Open the Roles page
                </Link>
              )}
            </CardContent>
          </Card>

          {/* OTP Settings */}
          <Card>
            <CardHeader>
              <CardTitle>OTP Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OTP Expiry (minutes)
                  </label>
                  <Input
                    {...register('otp_expiry_minutes', { 
                      required: 'OTP expiry is required',
                      min: { value: 1, message: 'Must be at least 1 minute' },
                      max: { value: 60, message: 'Must be at most 60 minutes' }
                    })}
                    type="number"
                    min="1"
                    max="60"
                    className={errors.otp_expiry_minutes ? 'border-red-500' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OTP Length
                  </label>
                  <Input
                    {...register('otp_length', { 
                      required: 'OTP length is required',
                      min: { value: 4, message: 'Must be at least 4 digits' },
                      max: { value: 8, message: 'Must be at most 8 digits' }
                    })}
                    type="number"
                    min="4"
                    max="8"
                    className={errors.otp_length ? 'border-red-500' : ''}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="px-8"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* SMTP Configuration Modal */}
      <Modal
        isOpen={showSMTPModal}
        onClose={() => setShowSMTPModal(false)}
        title="SMTP Configuration"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMTP Host
              </label>
              <Input
                value={smtpConfig.host}
                onChange={(e) => handleSMTPConfigChange('host', e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Port
              </label>
              <Input
                type="number"
                value={smtpConfig.port}
                onChange={(e) => handleSMTPConfigChange('port', parseInt(e.target.value))}
                placeholder="587"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <Input
              value={smtpConfig.username}
              onChange={(e) => handleSMTPConfigChange('username', e.target.value)}
              placeholder="your-email@gmail.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Address
            </label>
            <Input
              type="email"
              value={smtpConfig.from_address}
              onChange={(e) => handleSMTPConfigChange('from_address', e.target.value)}
              placeholder="noreply@yourbusiness.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <Input
              type="password"
              value={smtpConfig.password}
              onChange={(e) => handleSMTPConfigChange('password', e.target.value)}
              autoComplete="new-password"
              placeholder={smtpConfig.has_password ? 'Leave blank to keep current password' : 'Your email password or app password'}
            />
            {smtpConfig.has_password && (
              <p className="text-xs text-gray-500 mt-1">
                A password is already saved. Leave this blank to keep it, or enter a new one to replace it.
              </p>
            )}
          </div>
          
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={smtpConfig.use_tls}
                onChange={(e) => handleSMTPConfigChange('use_tls', e.target.checked)}
                className="mr-2"
              />
              Use TLS
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={smtpConfig.use_ssl}
                onChange={(e) => handleSMTPConfigChange('use_ssl', e.target.checked)}
                className="mr-2"
              />
              Use SSL
            </label>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowSMTPModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setShowSMTPModal(false)}
            >
              Save SMTP Config
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessInformationPage;
